import React, { useEffect, useState } from 'react';
import { apiService } from '../../services/api';
import { usePlan } from '../../hooks/usePlan';
import { loadPaddle } from '../../services/paddleLoader';
import CapacityLine from './CapacityLine';
import './pricingPage.css';

/*
 * PricingPage — modal overlay (SourceWizard overlay pattern) with the
 * free-vs-Plus tier cards (SaaS pricing-card layout mirroring
 * prelaunch-site/pricing.html) and the Paddle checkout button. Opened from:
 * the waitlist banner, the plan-lock hints in SourceWizard/ScheduleWizard/
 * ScheduleEditor (via utils/pricingModal's window-event bus), and the
 * "Upgrade" footer link in App.js (plan==='free' only).
 *
 * Props:
 *   user    — Cognito user object from App.js (user.username is the sub
 *             used elsewhere in this app as the userId; user.attributes /
 *             signInDetails carry the email if present).
 *   onClose — close the modal.
 */

const FREE_FEATURES = [
  '1 episode per day',
  'Episodes up to 30 minutes',
  'Popular news sources',
  'All languages & voices',
  'Episodes kept for 21 days'
];

const PLUS_FEATURES = [
  '3 episodes per day',
  'Episodes up to 60 minutes',
  'Your own custom news sources',
  'Remove intro/outro',
  'Priority generation',
  'Episodes kept for 120 days',
  'No weekly sign-in requirement'
];

function getEmail(user) {
  return user?.signInDetails?.loginId || user?.attributes?.email || undefined;
}

function formatPeriodEnd(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (error) {
    return null;
  }
}

function PricingPage({ user, onClose }) {
  const { plan, status, currentPeriodEnd, loading: planLoading, resolved: planResolved } = usePlan();

  const [config, setConfig] = useState(null); // {available, clientToken, priceMonth, priceYear, environment} | {available:false}
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(false);
  const [paddleReady, setPaddleReady] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [checkoutBusy, setCheckoutBusy] = useState(null); // 'month' | 'year' | null
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const [cycle, setCycle] = useState('month'); // 'month' | 'year' — billing-cycle toggle
  const [waitlistPosition, setWaitlistPosition] = useState(null); // exact queue position, waitlisted users only

  // Exact waitlist position (§9, free-tier-cap-enforcement plan) — cheap to fetch here since the
  // pricing modal is already open and showing waitlist copy; best-effort, failure just means the
  // position line doesn't show (the waitlist banner/badges already convey the state itself).
  useEffect(() => {
    if (status !== 'waitlisted') {
      setWaitlistPosition(null);
      return undefined;
    }
    let cancelled = false;
    apiService.getWaitlistPosition()
      .then((data) => {
        if (!cancelled && typeof data?.position === 'number') {
          setWaitlistPosition(data.position);
        }
      })
      .catch((error) => {
        console.error('PricingPage: failed to load waitlist position', error);
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  // "Manage billing" → server creates a Paddle customer-portal session → open it.
  // Cancellation happens on Paddle's hosted portal (we don't own that flow), so the only place
  // we can warn about waitlist consequences is right here, before we even open it (§9,
  // free-tier-cap-enforcement plan) — matches the window.confirm convention already used for
  // destructive actions (ScheduleCard's delete confirmation).
  const openBillingPortal = async () => {
    const proceed = window.confirm(
      "Heads up: if you cancel and the free tier is full, you'll rejoin the waitlist when your paid period ends. Continue to manage billing?"
    );
    if (!proceed) return;

    setPortalLoading(true);
    setPortalError(null);
    try {
      const res = await apiService.getBillingPortalLink();
      if (res?.url) {
        window.open(res.url, '_blank', 'noopener');
      } else {
        setPortalError(
          res?.reason === 'no_subscription'
            ? 'No billing account found — contact admin@linguanewscast.com.'
            : 'Billing portal is unavailable right now — please try again shortly.'
        );
      }
    } catch (error) {
      console.error('PricingPage: portal link failed', error);
      setPortalError('Billing portal is unavailable right now — please try again shortly.');
    } finally {
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    apiService.getBillingConfig()
      .then((data) => {
        if (cancelled) return;
        setConfig(data);
        setConfigLoading(false);

        if (data?.available && data.clientToken) {
          loadPaddle(data.environment, data.clientToken)
            .then(() => {
              if (!cancelled) setPaddleReady(true);
            })
            .catch((error) => {
              console.error('PricingPage: Paddle failed to load', error);
              if (!cancelled) setCheckoutError('Payments are temporarily unavailable — please try again shortly.');
            });
        }
      })
      .catch((error) => {
        console.error('PricingPage: failed to load billing config', error);
        if (!cancelled) {
          setConfigError(true);
          setConfigLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Esc closes the modal, matching the SourceWizard overlay convention.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose && onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const paymentsAvailable = Boolean(config?.available);
  // FIX 1: checkout requires the server-minted {sub, sig} binding. Without it, the webhook can't
  // prove the sub, so never open checkout.
  const canCheckout = paymentsAvailable && Boolean(config?.checkout) && paddleReady && !planLoading && plan === 'free';

  const openCheckout = (selectedCycle) => {
    if (!canCheckout || !window.Paddle) return;
    const priceId = selectedCycle === 'year' ? config.priceYear : config.priceMonth;
    if (!priceId) {
      setCheckoutError('Pricing is not configured yet — please try again shortly.');
      return;
    }
    setCheckoutError(null);
    setCheckoutBusy(selectedCycle);
    try {
      window.Paddle.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        // FIX 1: send the server-minted binding (sub + sig) rather than a client-chosen sub, so the
        // webhook can verify this checkout was bound to this authenticated user server-side.
        customData: { sub: config.checkout?.sub, sig: config.checkout?.sig },
        customer: getEmail(user) ? { email: getEmail(user) } : undefined
      });
    } catch (error) {
      console.error('PricingPage: Paddle.Checkout.open failed', error);
      setCheckoutError('Could not open checkout — please try again.');
    } finally {
      // Paddle's overlay takes over the UI; clear the busy flag once it's
      // had a chance to open (the eventCallback in paddleLoader.js handles
      // the actual close/refresh, this is just button-spinner hygiene).
      setCheckoutBusy(null);
    }
  };

  // Current-plan panel for Plus (incl. canceled_pending) and Comp users —
  // same logic as before the tier-card redesign, lightly restyled.
  const renderCurrentPlanPanel = () => {
    if (plan === 'comp') {
      return <div className="pricing-page__current-plan">You're on <strong>Comp</strong> — full access, no payment needed.</div>;
    }
    const renewalText = formatPeriodEnd(currentPeriodEnd);
    const statusText = status === 'canceled_pending'
      ? (renewalText ? `Plus until ${renewalText}, then back to Free.` : 'Plus — cancels at period end.')
      : (renewalText ? `Renews ${renewalText}.` : 'Active.');
    return (
      <div className="pricing-page__current-plan">
        You're on <strong>Plus</strong>. {statusText}
        <div className="pricing-page__portal-row">
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={portalLoading}
            onClick={openBillingPortal}
          >
            {portalLoading ? 'Opening…' : 'Manage billing'}
          </button>
          {portalError && <span className="pricing-page__portal-error">{portalError}</span>}
        </div>
      </div>
    );
  };

  // The single CTA on the Plus card, following the selected billing cycle.
  const renderPlusCta = () => {
    if (!planResolved && !planLoading) {
      return (
        <button type="button" className="pricing-tier__cta" disabled>
          Couldn't confirm your plan — refresh to try again
        </button>
      );
    }
    if (configLoading) {
      return <div className="pricing-page__cta-hint">Loading checkout…</div>;
    }
    // FIX 1: also treat a missing server-minted checkout binding as "not launched" (defensive) —
    // without {sub, sig} the webhook can't verify the purchaser, so don't offer checkout.
    if (configError || !paymentsAvailable || !config?.checkout) {
      return (
        <button type="button" className="btn btn-primary pricing-tier__cta" disabled>
          Payments launching soon
        </button>
      );
    }

    return (
      <>
        <button
          type="button"
          className="btn btn-primary pricing-tier__cta"
          disabled={!canCheckout || checkoutBusy !== null}
          onClick={() => openCheckout(cycle)}
        >
          {checkoutBusy !== null ? 'Opening…' : 'Upgrade to Plus'}
        </button>
        {!paddleReady && !checkoutError && (
          <div className="pricing-page__cta-hint">Preparing secure checkout…</div>
        )}
        {checkoutError && <div className="pricing-page__cta-error">{checkoutError}</div>}
      </>
    );
  };

  const renderTierCards = () => (
    <>
      <div className="pricing-cycle" role="group" aria-label="Billing cycle">
        <button
          type="button"
          className={`pricing-cycle__option${cycle === 'month' ? ' pricing-cycle__option--active' : ''}`}
          aria-pressed={cycle === 'month'}
          onClick={() => setCycle('month')}
        >
          Monthly
        </button>
        <button
          type="button"
          className={`pricing-cycle__option${cycle === 'year' ? ' pricing-cycle__option--active' : ''}`}
          aria-pressed={cycle === 'year'}
          onClick={() => setCycle('year')}
        >
          Yearly <span className="pricing-cycle__save">save 36%</span>
        </button>
      </div>

      <div className="pricing-tiers">
        <div className="pricing-tier pricing-tier--free">
          <div className="pricing-tier__head">
            <h3 className="pricing-tier__name">Free</h3>
            <span className="pricing-tier__chip">Your current plan</span>
          </div>
          <div className="pricing-tier__price-row">
            <span className="pricing-tier__price">$0</span>
          </div>
          <ul className="pricing-tier__list">
            {FREE_FEATURES.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
          {status === 'waitlisted' && (
            <p className="pricing-tier__price-note">
              You're on the waitlist{typeof waitlistPosition === 'number' ? ` — position #${waitlistPosition}` : ''}. Upgrade to skip the line.
            </p>
          )}
          <CapacityLine />
        </div>

        <div className="pricing-tier pricing-tier--plus">
          <div className="pricing-tier__head">
            <h3 className="pricing-tier__name">Plus</h3>
            {/* "Best value" refers to the yearly discount, so it only appears when Yearly is selected. */}
            {cycle === 'year' && (
              <span className="pricing-tier__chip pricing-tier__chip--accent">Best value</span>
            )}
          </div>
          <div className="pricing-tier__price-row">
            <span className="pricing-tier__price">{cycle === 'year' ? '$69' : '$8.99'}</span>
            <span className="pricing-tier__period">{cycle === 'year' ? '/yr' : '/mo'}</span>
          </div>
          <div className="pricing-tier__price-note">
            {cycle === 'year' ? 'Save 36% vs monthly' : 'or $69/yr — save 36%'}
          </div>
          <ul className="pricing-tier__list">
            {PLUS_FEATURES.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
          <div className="pricing-tier__cta-area">
            {renderPlusCta()}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="pricing-page__overlay" role="dialog" aria-modal="true" aria-label="LinguaNewsCast Plus pricing">
      <div className="pricing-page">
        <div className="pricing-page__header">
          <h2 className="pricing-page__title">LinguaNewsCast Plus</h2>
          <button type="button" className="pricing-page__close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="pricing-page__body">
          <p className="pricing-page__subtitle">
            More episodes, longer episodes, your own sources — no waitlist, no weekly sign-in.
          </p>

          {(planResolved && (plan === 'plus' || plan === 'comp')) ? renderCurrentPlanPanel() : renderTierCards()}
        </div>
      </div>
    </div>
  );
}

export default PricingPage;
